import React, { useState, useEffect, useMemo } from 'react';
import './BackgroundLine.css';

// Helper functions for calculating distributions
const gaussianPDF = (x, mean, stdDev) => {
  const variance = stdDev * stdDev;
  const numerator = Math.exp(-((x - mean) ** 2) / (2 * variance));
  const denominator = stdDev * Math.sqrt(2 * Math.PI);
  return numerator / denominator;
};

// Gaussian Mixture Model calculator
const gaussianMixture = (x, components) => {
  return components.reduce((sum, component) => {
    return sum + component.weight * gaussianPDF(x, component.mean, component.stdDev);
  }, 0);
};

// Parameters for our complex two-class GMM model
const MODEL_PARAMS = {
  class1: {
    // Class 1 (red) components - creates a "surrounding" pattern
    components: [
      // Left side cluster
      { mean: 0.23, stdDev: 0.025, weight: 0.15 },
      { mean: 0.28, stdDev: 0.02, weight: 0.1 },
      { mean: 0.32, stdDev: 0.015, weight: 0.12 },
      
      // Small peaks in the middle region
      { mean: 0.48, stdDev: 0.012, weight: 0.08 },
      { mean: 0.52, stdDev: 0.01, weight: 0.05 },
      { mean: 0.65, stdDev: 0.015, weight: 0.1 },
      
      // Right side cluster
      { mean: 0.78, stdDev: 0.02, weight: 0.1 },
      { mean: 0.85, stdDev: 0.025, weight: 0.15 },
      { mean: 0.93, stdDev: 0.035, weight: 0.15 }
    ]
  },
  class2: {
    // Class 2 (blue) components - concentrated in central regions with some islands
    components: [
      // Isolated island on the left
      { mean: 0.12, stdDev: 0.02, weight: 0.1 },
      
      // Main central region
      { mean: 0.4, stdDev: 0.035, weight: 0.15 },
      { mean: 0.45, stdDev: 0.025, weight: 0.1 },
      { mean: 0.55, stdDev: 0.03, weight: 0.2 },
      { mean: 0.6, stdDev: 0.025, weight: 0.15 },
      { mean: 0.7, stdDev: 0.02, weight: 0.1 },
      
      // Small isolated island on the right
      { mean: 0.98, stdDev: 0.015, weight: 0.1 },
      
      // Narrow spikes that create rapid transitions
      { mean: 0.35, stdDev: 0.008, weight: 0.05 },
      { mean: 0.75, stdDev: 0.008, weight: 0.05 }
    ]
  },
  prior: 0.5 // Equal prior probability for both classes
};

// Predefined functions that can be used
export const Functions = {
  // Normal distribution with mean 0.8 and std 0.1
  normalDist: (x) => {
    return gaussianPDF(x, 0.8, 0.01);
  },
  
  // Cubic function
  cubic: (x) => {
    return 0.8 * Math.pow(x - 0.5, 3);
  },
  
  // Sine wave
  sine: (x) => {
    return 0.1 * Math.sin(x * Math.PI * 4);
  },
  
  // Parabola
  parabola: (x) => {
    return 0.5 * (x - 0.5) * (x - 0.5);
  },
  
  // CLASS DISTRIBUTIONS - ENHANCED WITH GMMs
  
  // Class 1 likelihood - P(X|C=1) - now a GMM
  class1Likelihood: (x) => {
    return gaussianMixture(x, MODEL_PARAMS.class1.components);
  },
  
  // Class 2 likelihood - P(X|C=2) - now a GMM
  class2Likelihood: (x) => {
    return gaussianMixture(x, MODEL_PARAMS.class2.components);
  },
  
  // Marginal distribution - P(X) = P(X|C=1)P(C=1) + P(X|C=2)P(C=2)
  marginalX: (x) => {
    const likelihood1 = gaussianMixture(x, MODEL_PARAMS.class1.components);
    const likelihood2 = gaussianMixture(x, MODEL_PARAMS.class2.components);
    
    return (likelihood1 * MODEL_PARAMS.prior) + (likelihood2 * MODEL_PARAMS.prior);
  },
  
  // Posterior for class 1 - P(C=1|X) using Bayes' rule
  posteriorClass1: (x) => {
    const likelihood1 = gaussianMixture(x, MODEL_PARAMS.class1.components);
    const likelihood2 = gaussianMixture(x, MODEL_PARAMS.class2.components);
    
    const numerator = likelihood1 * MODEL_PARAMS.prior;
    const denominator = numerator + (likelihood2 * MODEL_PARAMS.prior);
    
    return numerator / denominator;
  },
  
  // Posterior for class 2 - P(C=2|X) using Bayes' rule
  posteriorClass2: (x) => {
    const likelihood1 = gaussianMixture(x, MODEL_PARAMS.class1.components);
    const likelihood2 = gaussianMixture(x, MODEL_PARAMS.class2.components);
    
    const numerator = likelihood2 * MODEL_PARAMS.prior;
    const denominator = (likelihood1 * MODEL_PARAMS.prior) + numerator;
    
    return numerator / denominator;
  }
};

function BackgroundLine({ 
  yOffset = 160, 
  functionType = Functions.normalDist, 
  amplitude = 160,
  strokeWidth = 1,
  strokeColor = "rgba(0, 0, 0, 0.07)",
  steps = 200
}) {
  const [dimensions, setDimensions] = useState({ 
    width: window.innerWidth, 
  });
  
  // Generate path data efficiently with useMemo
  const pathData = useMemo(() => {
    const { width } = dimensions;
    const points = [];
    
    // Combine max finding and path generation in a single loop
    let maxValue = 0;
    const tempPoints = [];
    
    // First pass - find the max value
    for (let i = 0; i <= steps; i++) {
      const x = i / steps;
      const value = Math.abs(functionType(x));
      maxValue = Math.max(maxValue, value);
      tempPoints.push({ x, value: functionType(x) });
    }
    
    // Use max value of 1 if all values are 0
    maxValue = maxValue || 1;
    
    // Second pass - generate normalized points
    for (const point of tempPoints) {
      const normalizedY = -0.5 * (point.value / maxValue);
      const scaledX = point.x * width;
      const scaledY = yOffset + (normalizedY * amplitude);
      points.push(`${scaledX},${scaledY}`);
    }
    
    return `M${points.join(' L')}`;
  }, [dimensions, yOffset, amplitude, functionType, steps]);
  
  // Handle window resize with throttling
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, 100); // Throttle resize events
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);
  
  return (
    <div className="background-container">
      <svg
        className="function-line"
        width={dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="none"
      >
        <path
          d={pathData}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
      </svg>
    </div>
  );
}

export default BackgroundLine; 